import clsx from "clsx";
import React, { forwardRef, useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";

interface PasswordProps {
	id: string;
	label: string;
	className?: string;
	placeholder?: string;
	error?: string;
	maxLength?: number;
	optional?: string;
	autoComplete?: string;
	showValue?: boolean;
	onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
	onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
	onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
}

export const Password = forwardRef<HTMLInputElement, PasswordProps>(
	(props, ref) => {
		const {
			id,
			label,
			className,
			placeholder,
			error,
			maxLength,
			optional,
			autoComplete,
			showValue,
			onChange,
			onBlur,
			onKeyDown,
		} = props;

		const { formatMessage } = useIntl();

		const [valueVisible, toggleValueVisible] = useState(false);

		return (
			<div
				className={clsx({
					[`${className}`]: true,
					error: error,
					withAffix: showValue,
				})}
			>
				<label className="label" htmlFor={id}>
					{label}
					{optional && <span className="optional">{optional}</span>}
				</label>
				<input
					ref={ref}
					onChange={onChange}
					onBlur={onBlur}
					onKeyDown={onKeyDown}
					className="field"
					id={id}
					placeholder={placeholder}
					type={valueVisible ? "text" : "password"}
					maxLength={maxLength}
					aria-invalid={!!error}
					autoComplete={autoComplete}
					data-automationid={id}
					data-testid={`${id}.input`}
				/>
				{showValue && (
					<button
						type="button"
						className={clsx({
							showValue: true,
							error: error,
						})}
						onClick={(e) => {
							e.preventDefault();
							toggleValueVisible(!valueVisible);
						}}
					>
						{formatMessage({
							id: `profile.editpassword.${valueVisible ? "hide" : "show"}`,
						})}
					</button>
				)}
				{error && (
					<span
						className="errorMessage"
						data-automationid={`${id}.error`}
						data-testid={`${id}.error`}
					>
						<FormattedMessage id={error} values={{ fieldname: label }} />
					</span>
				)}
			</div>
		);
	},
);
